import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { Provider } from 'react-redux';
import { getStore } from 'store/store';
import { ScrollbarUtils } from 'utilities/ScrollbarUtils';

export type DialogAsMethodRedererProps = {
    isOpened: boolean;
    onClose(): void;
};

type DialogComponentStandardProps = {
    onClose?(): void;
};

type DialogProps<OwnProps> = {
    dialogComponentProps?: OwnProps;
    DialogComponent: React.ComponentType<OwnProps & DialogAsMethodRedererProps>;
    noScrollManagement?: boolean;
    dialogId: number;
};
type State = {
    isOpened: boolean;
};
class Dialog<
    OwnProps extends DialogComponentStandardProps
> extends React.PureComponent<DialogProps<OwnProps>, State> {
    state: State = {
        isOpened: true,
    };

    _handleClose = () => {
        const { onClose } = this.props.dialogComponentProps || {};
        if (onClose) {
            onClose();
        }
        this._close();
    };

    _close = () => {
        // need this to avoid Warning: Can't call setState (or forceUpdate) on an unmounted component
        this.setState(
            {
                isOpened: false,
            },
            () => {
                setTimeout(() => {
                    _removeDialog(
                        this.props.dialogId,
                        this.props.noScrollManagement,
                    );
                }, 0);
            },
        );
    };

    render() {
        const {
            dialogComponentProps = {} as OwnProps,
            DialogComponent,
        } = this.props;
        return (
            <DialogComponent
                {...dialogComponentProps}
                isOpened={this.state.isOpened}
                onClose={this._handleClose}
            />
        );
    }
}

const _removeDialog = (id, noScrollManagement) => {
    const target = document.getElementById(`dialog-as-method-renderer-${id}`);
    if (!target) {
        return;
    }
    unmountComponentAtNode(target);
    target.parentNode && target.parentNode.removeChild(target);
    if (!noScrollManagement) {
        ScrollbarUtils.showScroll();
    }
};

let idCounter = 0;

// TODO: fix type in Renderer word
export const DialogAsMethodRederer = {
    renderDialog<OwnProps>({
        DialogComponent,
        dialogComponentProps = {} as OwnProps,
        noScrollManagement,
    }: Omit<DialogProps<OwnProps>, 'dialogId'>) {
        const divTarget = document.createElement('div');
        const id = idCounter++;

        if (!noScrollManagement) {
            ScrollbarUtils.hideScroll();
        }

        divTarget.id = `dialog-as-method-renderer-${id}`;
        document.body.appendChild(divTarget);
        render(
            <Provider store={getStore()}>
                <Dialog<OwnProps>
                    DialogComponent={DialogComponent}
                    dialogId={id}
                    dialogComponentProps={dialogComponentProps}
                    noScrollManagement={noScrollManagement}
                />
            </Provider>,
            divTarget,
        );

        return () => _removeDialog(id, noScrollManagement);
    },

    makeDialogRenderer<OwnProps = {}>(
        DialogComponent: React.ComponentType<
            OwnProps & DialogAsMethodRedererProps
        >,
    ) {
        return {
            render: (props?: OwnProps) => {
                return DialogAsMethodRederer.renderDialog<OwnProps>({
                    dialogComponentProps: props,
                    DialogComponent,
                });
            },
        };
    },
};
